import { useState, useEffect, useRef } from 'react';
import type { DrawingSubmission } from '@twitch-draw/shared';
import { SubmissionPreview } from './SubmissionPreview';

interface ModerationPanelProps {
  submissions: DrawingSubmission[];
  onApprove: (submissionId: string) => void;
  onReject: (submissionId: string) => void;
  busyMap: Record<string, boolean>;
  error?: string | null;
}

export function ModerationPanel({
  submissions,
  onApprove,
  onReject,
  busyMap,
  error,
}: ModerationPanelProps) {
  const [visible, setVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const hasContent = submissions.length > 0 || !!error;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (hasContent) {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      setShouldRender(true);
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
      timerRef.current = setTimeout(() => setShouldRender(false), 300);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [hasContent]);

  if (!shouldRender) return null;

  return (
    <div className={`moderation-panel ${visible ? '' : 'moderation-panel--empty'}`}>
      <h4>Pending Drawings</h4>
      {error && <div className="moderation-panel__error">{error}</div>}
      {submissions.length === 0 && <p className="moderation-panel__empty">No pending submissions</p>}
      {submissions.map((submission) => {
        const isBusy = Boolean(busyMap[submission.id]);
        return (
          <div key={submission.id} className="moderation-panel__item">
            <SubmissionPreview strokes={submission.strokes} animation={submission.animation} />
            <div className="moderation-panel__info">
              <div className="moderation-panel__name">@{submission.displayName}</div>
              <div className="moderation-panel__meta">
                ID: {submission.userId} • {submission.bitsUsed} Bits • {new Date(submission.createdAt).toLocaleTimeString()}
              </div>
              <div className="moderation-panel__actions">
                <button onClick={() => onApprove(submission.id)} disabled={isBusy}>
                  Approve
                </button>
                <button onClick={() => onReject(submission.id)} disabled={isBusy}>
                  Reject
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
